import type { PaymentLinkData } from '../types';

/**
 * Encode payment data to base64 string for URL
 */
export function encodePaymentData(data: PaymentLinkData): string {
  const payload = {
    to: data.to,
    amount: data.amount,
    network: data.network || 'base',
    token: data.token || 'USDC',
    ...(data.memo && { memo: data.memo }),
    ...(data.priority && { priority: data.priority }),
  };
  
  return btoa(JSON.stringify(payload));
}

/**
 * Decode payment data from base64 string
 */
export function decodePaymentData(encoded: string): PaymentLinkData | null {
  try {
    const decoded = atob(encoded);
    const data = JSON.parse(decoded);
    
    // Validate required fields
    if (!data.to || !data.amount) {
      return null;
    }
    
    return {
      to: data.to,
      amount: data.amount,
      network: data.network || 'base',
      token: data.token || 'USDC',
      memo: data.memo,
      priority: data.priority,
    };
  } catch (error) {
    console.error('Failed to decode payment data:', error);
    return null;
  }
}

/**
 * Parse payment link from URL
 */
export function parsePaymentLink(url: string): PaymentLinkData | null {
  try {
    const urlObj = new URL(url);
    
    // Check query param
    const x402Param = urlObj.searchParams.get('x402');
    if (x402Param) {
      return decodePaymentData(x402Param);
    }
    
    // Check hash fragment
    if (urlObj.hash.startsWith('#x402:')) {
      const encoded = urlObj.hash.slice(6); // Remove '#x402:'
      return decodePaymentData(encoded);
    }
    
    return null;
  } catch (error) {
    console.error('Failed to parse payment link:', error);
    return null;
  }
}

/**
 * Validate payment link data
 */
export function validatePaymentData(data: PaymentLinkData): { valid: boolean; error?: string } {
  // Validate recipient address
  if (!data.to || !data.to.startsWith('0x') || data.to.length !== 42) {
    return { valid: false, error: 'Invalid recipient address' };
  }
  
  // Validate amount
  const amount = parseFloat(data.amount);
  if (isNaN(amount) || amount <= 0) {
    return { valid: false, error: 'Invalid amount' };
  }
  
  // Validate network
  const validNetworks = ['base', 'optimism', 'arbitrum', 'ethereum'];
  if (data.network && !validNetworks.includes(data.network.toLowerCase())) {
    return { valid: false, error: `Invalid network: ${data.network}` };
  }
  
  return { valid: true };
}

